import lodash from 'lodash';
import moment from 'moment';

const PRINCIPAL_INVESTIGATOR_ROLE = 'Principal Investigator';

function getTaskIndex(taskId, studyTasksList) {
    const matchingTask = studyTasksList.find((task) => task.id === taskId);    
    return matchingTask && matchingTask.taskIndex != null
        ? matchingTask.taskIndex
        : null;
}

function getTaskName(taskId, studyTasksList) {
    const matchingTask = studyTasksList.find((task) => task.id === taskId);    
    return matchingTask ? matchingTask.taskName : null;
}

export const delegationsList = (state) => {
    const doaStudyDelegations = lodash.get(state, 'doaStudyDelegations', null);
    const doaStudyTasks = lodash.get(state, 'doaStudyTasks', null);

    if (!doaStudyDelegations || !doaStudyTasks) {
        return null;
    }

    let usersWithTasks = [];
    if (doaStudyDelegations.length === 0 || doaStudyTasks.length === 0) {
        return usersWithTasks;
    }

    for (var i = 0; i < doaStudyDelegations.length; i++) {
        const delegation = doaStudyDelegations[i];
        const tasks = [];
        for (var j = 0; j < delegation.taskIds.length; j++) {
            const currentTaskId = delegation.taskIds[j];
            tasks.push({
                taskId: currentTaskId,
                taskIndex: getTaskIndex(currentTaskId, doaStudyTasks),
                taskName: getTaskName(currentTaskId, doaStudyTasks),
            });
        }

        usersWithTasks.push({
            id: delegation.id,
            user: delegation.user,
            role: delegation.rolesManagement,
            tasks: tasks,
            startDate: delegation.startDate,
            endDate: delegation.endDate,
            signatures: delegation.signatures,
        });
    }
    return lodash.orderBy(usersWithTasks, 'startDate', 'asc');
};

export const delegationsListV2 = (state) => {
    const doaStudyDelegations = lodash.get(state, 'doaStudyDelegations', null);
    const doaStudyTasks = lodash.get(state, 'doaStudyTasks', null);

    if (!doaStudyDelegations || !doaStudyTasks || doaStudyDelegations.length === 0 || doaStudyTasks.length === 0) {
        return [];
    }

    const tasksMap = doaStudyTasks.reduce((acc, task) => {
        acc[task.id] = {
            taskIndex: task.taskIndex,
            taskName: task.taskName,
        };
        return acc;
    }, {});

    const usersWithTasks = doaStudyDelegations.map((delegation) => {
        const tasks = delegation.taskIds.map((currentTaskId) => {
            const foundTask = tasksMap[currentTaskId];
            return {
                taskId: currentTaskId,
                taskIndex: foundTask ? foundTask.taskIndex : null,
                taskName: foundTask ? foundTask.taskName : null,
            };
        });

        return {
            id: delegation.id,
            user: delegation.user,
            role: delegation.rolesManagement,
            tasks: tasks,
            startDate: delegation.startDate,
            endDate: delegation.endDate,
            signatures: delegation.signatures,
        };
    });

    return usersWithTasks;
};

export const hasBinderAccess = (state) => {
    const binder = lodash.get(state, 'binder', {});
    const allUsersWithBinderAccess = lodash.get(binder, 'users', []);
    const internalUsersWithBinderAccess = allUsersWithBinderAccess.filter((user) => user.institutionId !== null);
    return internalUsersWithBinderAccess;
};

export const studyHasDelegatedPI = (state) => {
    const delegations = lodash.get(state, 'doaStudyDelegations', []);
    const unendedPIDelegations = delegations.filter(
        (delegation) => delegation.role === PRINCIPAL_INVESTIGATOR_ROLE && !delegation.endDate
    );
    return !lodash.isEmpty(unendedPIDelegations);
};

export function getActivePI(state) {
    const delegations = lodash.get(state, 'doaStudyDelegations', []);
    const piDelegations = delegations.filter((delegation) => delegation.role === PRINCIPAL_INVESTIGATOR_ROLE);
    const activePIDelegation = piDelegations.find((delegation) => {
        const now = moment();
        const start = moment(delegation.startDate, 'YYYY-MM-DD');
        const end = moment(delegation.endDate, 'YYYY-MM-DD');
        if (now.isAfter(start)) {
            if (!delegation.endDate) {
                return true;
            } else {
                return now.isSameOrBefore(end, 'day');
            }
        }
    });
    return activePIDelegation ? activePIDelegation : null;
}

export function isActivePIDelegation(state, delegationId) {
    const activePIDelegation = getActivePI(state);
    if (!activePIDelegation) {
        return false;
    }

    return activePIDelegation.id === delegationId;
}

export const getMostRecentDelegationForUser = (state, userId) => {
    if (!userId) {
        return null;
    }

    const delegations = lodash.get(state, 'doaStudyDelegations', []);
    const userDelegations = delegations.filter((delegation) => delegation.user.id === userId);

    if (lodash.isEmpty(userDelegations)) {
        return null;
    }
    return userDelegations[userDelegations.length - 1];
};

// Return all delegations which overlap with the PIs tenure
export const getDelegationsUnderPI = (delegations, currentPIDelegation) => {
    return delegations.filter((delegation) => {
        // skip PI delegations not for the current PI
        if (delegation.role === PRINCIPAL_INVESTIGATOR_ROLE && delegation.user.id !== currentPIDelegation.user.id) {
            return;
        }

        const delegationStartsBeforePIEnds = moment(delegation.startDate).isSameOrBefore(currentPIDelegation.endDate);
        const delegationEndsAfterPIStarts = moment(delegation.endDate).isSameOrAfter(
            moment(currentPIDelegation.startDate)
        );

        const delegationStartsBefore = currentPIDelegation.endDate === null || delegationStartsBeforePIEnds;
        const delegationEndsAfter = delegation.endDate === null || delegationEndsAfterPIStarts;

        return delegationStartsBefore && delegationEndsAfter;
    });
};

export const getUser = (state, userId) => {
    const binder = lodash.get(state, 'binder', {});
    if (!binder) {
        return null;
    }

    return binder.users.find((u) => u.id === userId);
};

export const getCurrentUserId = (state) => {
    const me = lodash.get(state, 'me', {});
    const binder = lodash.get(state, 'binder', {});

    if (!me || !binder.users) {
        return null;
    }
    return binder.users.find((u) => u.sub === me.sub).id;
};

export const getUserIsPI = (state, userId) => {
    const activePIUser = getActivePI(state);
    return activePIUser && activePIUser.user.id === userId;
};

export const getUserWillBecomePI = (state, userId) => {
    const mostRecentDelegation = getMostRecentDelegationForUser(state, userId);
    return mostRecentDelegation && mostRecentDelegation.role === PRINCIPAL_INVESTIGATOR_ROLE;
};

export const getUserHasActionableDelegations = (state, userId) => {
    const actionableDelegations = getActionableDelegationsForUser(state, userId);
    return !lodash.isEmpty(actionableDelegations);
};

export const getUnfulfilledSignatureExpectationsForUser = (state, userId) => {
    const signatureExpectations = lodash.get(state, 'doaSignatureExpectations', []);
    return signatureExpectations.filter(
        (expectation) => expectation.user.id === userId && expectation.signedAt === null
    );
};

export const getActionableDelegationsForUser = (state, userId) => {
    const actionableDelegationsForUser = getUnfulfilledSignatureExpectationsForUser(state, userId);
    const delegationsExpectingSignature = new Set(
        actionableDelegationsForUser.map((expectation) => expectation.delegationId)
    );

    return Array.from(delegationsExpectingSignature);
};

export const getSignatureExpectationsForDelegation = (state, delegationId) => {
    if (!delegationId) {
        return [];
    }

    const signatureExpectations = lodash.get(state, 'doaSignatureExpectations', []);
    return signatureExpectations.filter((expectation) => expectation.delegationId === delegationId);
};

export const getIsDelegationActionableForUser = (state, userId, delegationId) => {
    const signatureExpectations = lodash.get(state, 'doaSignatureExpectations', []);
    const signatureExpectationsForUser = signatureExpectations.filter(
        (expectation) => expectation.user.id === userId && expectation.signedAt === null
    );
    return signatureExpectationsForUser.some((expectation) => expectation.delegationId === delegationId);
};

export const isCentralized = (state) => {
    const isCentralized = lodash.get(state, 'me.isInstitutionCentralized');
    return isCentralized === 'true';
};

function getTestamentText(testaments, role, defaultTestament) {
    let testament;

    let customTestament = testaments.find((t) => t.title === role);
    if (customTestament) {
        testament = customTestament.text;
    } else {
        testament = defaultTestament;
    }

    return testament.trim();
}

export const getTestamentTextForUser = (state, userId, defaultStaffTestament, defaultPITestament) => {
    const testaments = lodash.get(state, 'doaTestaments', []);
    const userIsPI = getUserIsPI(state, userId);
    const userWillBecomePI = getUserWillBecomePI(state, userId);

    if (userIsPI || userWillBecomePI) {
        return getTestamentText(testaments, 'PI', defaultPITestament);
    } else {
        return getTestamentText(testaments, 'Staff', defaultStaffTestament);
    }
};

export const getUndelegatedUsers = (users, delegations) => {
    if (!users || lodash.isEmpty(users)) {
        return [];
    }

    let selectableUsers = users;

    if (!lodash.isEmpty(delegations)) {
        const usersWithOpenDelegations = users.filter((user) =>
            delegations.find((delegation) => delegation.user.id === user.id && !delegation.endDate)
        );
        selectableUsers = users.filter((user) => !usersWithOpenDelegations.map((u) => u.id).includes(user.id));
    }

    return selectableUsers.sort((userA, userB) => {
        if (userA.lastName < userB.lastName) {
            return -1;
        }
        if (userA.lastName > userB.lastName) {
            return 1;
        }
        return 0;
    });
};

export const getUndelegatedUsersPlusActivePI = (users, delegations, currentPIUserId) => {
    if (!users || lodash.isEmpty(users)) {
        return [];
    }

    if (lodash.isEmpty(delegations)) {
        return lodash.sortBy(users, ['lastName']);
    }

    const delegationGroup = lodash.groupBy(delegations, 'user.id');
    let selectableUsers = [];

    users.forEach((user) => {
        if (user.id === currentPIUserId) {
            return selectableUsers.push(user);
        }

        const userDelegations = delegationGroup[user.id];

        if (lodash.isEmpty(userDelegations)) {
            return selectableUsers.push(user);
        }

        const allDelegationsCompleted = userDelegations.every((userDelegation) => userDelegation.endDate);

        if (allDelegationsCompleted) {
            return selectableUsers.push(user);
        }
    });

    return lodash.sortBy(selectableUsers, ['lastName']);
};

export const getDelegationTaskOptions = (state) => {
    const allTasks = lodash.get(state, 'doaStudyTasks', []);

    return lodash.map(allTasks, (task) => ({
        label: `${lodash.get(task, 'taskIndex')}. ${lodash.get(task, 'taskName')}`,
        value: lodash.get(task, 'id'),
        task,
    }));
};

export const getDelegationRoleOptions = (state) => {
    const PI_ROLE = 'Principal Investigator';
    const allRoles = lodash.get(state, 'doaRoles', []);
    const rolePI = lodash.find(allRoles, ({ name }) => name === PI_ROLE);
    const roleNonPI = lodash
        .chain(allRoles)
        .filter(({ name }) => name !== PI_ROLE)
        .sort((a, b) => lodash.get(a, 'name', '').localeCompare(lodash.get(b, 'name', '')))
        .value();
    return lodash.map([rolePI, ...roleNonPI], (role) => ({
        label: lodash.get(role, 'name'),
        value: lodash.get(role, 'name'),
        role,
    }));
};

export const alreadyDelegatedStaff = (state) => {
    function predicate(startDate, endDate) {
        const endNotDefined = lodash.isNil(endDate);
        const notYetEnd = moment().isSameOrBefore(new Date(endDate));

        return endNotDefined || notYetEnd;
    }

    const allDOA = lodash.get(state, 'doaStudyDelegations', []);

    return lodash.map(
        lodash.filter(allDOA, ({ startDate, endDate }) => predicate(startDate, endDate)),
        (doa) => lodash.get(doa, 'user')
    );
};

export const getDelegationAvailableUserOptions = (state) => {
    const hasAccessInternalUsers = hasBinderAccess(state);
    const alreadyDelegatedStaffs = alreadyDelegatedStaff(state);
    const uniqueIds = new Set(lodash.map(alreadyDelegatedStaffs, (user) => user.id));
    const availabelUsers = lodash.filter(hasAccessInternalUsers, (user) => !uniqueIds.has(user.id));

    return lodash.map(availabelUsers, (user) => ({ label: user.displayName, value: user.id, user }));
};
